import { useCallback, useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { createLinkToken, exchangePublicToken, type Session } from "@sdcp/api-client";
import { Button, type ButtonVariant } from "@sdcp/ui";

export interface ConnectBankButtonProps {
  requirementId: string;
  label: string;
  busy?: boolean;
  variant?: ButtonVariant;
  onConnected: (session: Session) => void;
  onUnavailable: (message: string) => void;
}

/**
 * Opens Plaid Link so the borrower can choose their own bank and sign in.
 *
 * The bank password is typed into Plaid's interface, never ours — we only ever
 * receive a short-lived public token. That is what makes this path different
 * from the insurance portals, where no such intermediary exists.
 */
export function ConnectBankButton({
  requirementId,
  label,
  busy = false,
  variant = "primary",
  onConnected,
  onUnavailable,
}: ConnectBankButtonProps) {
  const [token, setToken] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  // The token is fetched when the borrower asks to connect, not on page load.
  // Link tokens expire, and most borrowers reach this screen and upload instead.
  const begin = useCallback(async () => {
    setWorking(true);
    try {
      const { link_token } = await createLinkToken(requirementId);
      setToken(link_token);
    } catch (error) {
      setWorking(false);
      onUnavailable(
        error instanceof Error ? error.message : "Bank connection is unavailable right now.",
      );
    }
  }, [requirementId, onUnavailable]);

  const { open, ready } = usePlaidLink({
    // Null until the borrower asks to connect — the library treats that as
    // "not initialised", which is what we want before a token exists.
    token,
    onSuccess: async (publicToken, metadata) => {
      // The library types this as nullable. A success callback without a token
      // is not something we can act on, and silently continuing would send an
      // empty exchange request.
      if (!publicToken) {
        setWorking(false);
        setToken(null);
        onUnavailable("Your bank did not finish connecting. Please try again.");
        return;
      }
      try {
        const session = await exchangePublicToken(requirementId, {
          public_token: publicToken,
          institution_id: metadata.institution?.institution_id,
          institution_name: metadata.institution?.name,
        });
        onConnected(session);
      } catch (error) {
        onUnavailable(
          error instanceof Error ? error.message : "Could not finish connecting your bank.",
        );
      } finally {
        setWorking(false);
        setToken(null);
      }
    },
    onExit: () => {
      // Abandoning Plaid is not a failure. The borrower can try again or upload,
      // and nothing about the requirement changes.
      setWorking(false);
      setToken(null);
    },
  });

  useEffect(() => {
    if (token && ready) open();
  }, [token, ready, open]);

  return (
    <Button variant={variant} onClick={begin} disabled={busy || working}>
      {working ? "Opening your bank…" : label}
    </Button>
  );
}
